'use client';

import * as React from 'react';
import * as ContextMenuPrimitive from '@radix-ui/react-context-menu';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, ChevronRight } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useNestedDismissableLayer } from '../overlayStack';

/** The shadcn/ui ContextMenu, on Radix `@radix-ui/react-context-menu`.
 *
 *  This file is the shadcn component itself — anatomy, `data-slot` attributes and prop surface are
 *  shadcn's; the styling is ours and reads this app's design tokens.
 *
 *  Behaviour is Radix's and is NOT reimplemented here: opening at the pointer, viewport-aware placement
 *  and submenu flipping, roving focus, typeahead, Escape, outside-press dismissal and `aria-*` wiring.
 *  `components/ui/ContextMenu.tsx` is the app-shaped wrapper over these parts.
 *
 *  A context menu is denser than a dropdown by design (this is the file-tree / register right-click
 *  menu), so its rows are smaller than `dropdown-menu.tsx`'s — but the highlight is spelled exactly the
 *  same way, because that spelling is the part that has to be identical everywhere. */

function ContextMenu({ ...props }: React.ComponentProps<typeof ContextMenuPrimitive.Root>) {
  return <ContextMenuPrimitive.Root data-slot="context-menu" {...props} />;
}

function ContextMenuTrigger({ ...props }: React.ComponentProps<typeof ContextMenuPrimitive.Trigger>) {
  return <ContextMenuPrimitive.Trigger data-slot="context-menu-trigger" {...props} />;
}

function ContextMenuSub({ ...props }: React.ComponentProps<typeof ContextMenuPrimitive.Sub>) {
  return <ContextMenuPrimitive.Sub data-slot="context-menu-sub" {...props} />;
}

/** Both floating panels — the menu and each submenu — wear the same surface. `max-h` is in `dvh` on
 *  purpose: a mobile browser's collapsing toolbar makes `vh` taller than the screen, so a long submenu
 *  measured in `vh` runs off the bottom instead of scrolling. */
const menuPanel = cn(
  // `overlay-layer-menu` is the app's menu z-index band; the stacking order lives once in tokens.css.
  'overlay-layer-menu max-h-[60dvh] overflow-y-auto overflow-x-hidden',
  // Radix places a context panel BESIDE its point and corrects a horizontal overflow only by flipping to
  // the other side (its shift never moves a side-placed panel sideways). On a phone, a press near the
  // middle leaves room on neither side, so the panel takes the width Radix measured on the side it chose
  // and its rows wrap, instead of running off that edge of the screen.
  'max-w-(--radix-context-menu-content-available-width) min-w-[min(--spacing(44),var(--radix-context-menu-content-available-width))]',
  // Radius 10, matching `shadcn/dropdown-menu.tsx`. The two menu families are one thing to a reader — a
  // panel of actions attached to what it was opened from — and a 12px context menu beside a 10px
  // dropdown is a difference nothing means. The padding and the quieter shadow stay this file's own: a
  // context menu is denser and opens at the pointer rather than off a trigger.
  'rounded-md border border-border bg-popover p-1 text-ui text-popover-foreground shadow-card',
  'data-[state=open]:animate-fade-up',
);

function ContextMenuContent({
  className = '',
  ref,
  ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.Content>) {
  // The open panel counts as a nested layer of the modal that owns it, like select, dropdown and popover
  // content do, so a backdrop press dismisses the menu and leaves the modal standing. A submenu does not
  // register: it can only be open while its parent panel is, so counting it would be counting twice.
  const layerRef = useNestedDismissableLayer(ref);
  return (
    // DELIBERATELY NOT PORTALED, unlike stock shadcn. This app isolates the background of an open
    // modal by marking every other child of <body> `inert` and `aria-hidden`
    // (`components/ui/overlayStack.ts`), and it traps focus inside the dialog. A portaled menu is a new
    // child of <body>, so it lands outside the dialog it visually belongs to: the focus trap pulls
    // focus back out of it, and the next overlay to open marks it inert. That is not hypothetical here
    // — this menu is opened from inside the sessions dialog. Radix positions the panel with
    // `position: fixed`, so rendering in place still escapes an ancestor's overflow.
    // Re-running `shadcn add context-menu` will put the Portal back — that is the one line to remove.
    <ContextMenuPrimitive.Content
      ref={layerRef}
      data-slot="context-menu-content"
      className={cn(menuPanel, className)}
      {...props}
    />
  );
}

function ContextMenuSubContent({
  className = '',
  ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.SubContent>) {
  return (
    // Not portaled, for the same reason as the root content above.
    <ContextMenuPrimitive.SubContent
      data-slot="context-menu-sub-content"
      className={cn(menuPanel, className)}
      {...props}
    />
  );
}

/** The row. The highlight is `bg-accent` — in this app a wash of the FOREGROUND, not a step up the
 *  surface ramp. A surface step is a highlight a design can silently erase, and both designs here do
 *  erase it, which is how this row used to highlight invisibly. A skin may collapse the surface ramp
 *  but may not lower text contrast, so the wash reads by construction. */
const contextMenuItemVariants = cva(
  cn(
    'overlay-menu-item relative flex w-full cursor-default select-none items-center gap-2 rounded-md px-2.5 py-1.5',
    'text-left outline-none transition-colors',
    'data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ),
  {
    variants: {
      variant: {
        default: 'text-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
        destructive: 'text-destructive data-[highlighted]:bg-destructive/10 data-[highlighted]:text-destructive',
      },
      inset: {
        true: 'pl-7',
        false: '',
      },
    },
    defaultVariants: { variant: 'default', inset: false },
  },
);

function ContextMenuItem({
  className = '',
  variant,
  inset,
  ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.Item> & VariantProps<typeof contextMenuItemVariants>) {
  return (
    <ContextMenuPrimitive.Item
      data-slot="context-menu-item"
      data-variant={variant ?? 'default'}
      className={cn(contextMenuItemVariants({ variant, inset }), className)}
      {...props}
    />
  );
}

/** A toggle row: the same row as `ContextMenuItem`, `menuitemcheckbox` with `aria-checked`, and the
 *  check trailing it in the primary ink the select menus use for their pick. The lead slot stays the
 *  row's icon, so a toggle with an icon (a label's colour dot) keeps both. */
function ContextMenuCheckboxItem({
  className = '',
  variant,
  children,
  ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.CheckboxItem> & VariantProps<typeof contextMenuItemVariants>) {
  return (
    <ContextMenuPrimitive.CheckboxItem
      data-slot="context-menu-checkbox-item"
      data-variant={variant ?? 'default'}
      className={cn(contextMenuItemVariants({ variant }), className)}
      {...props}
    >
      {children}
      <span className="ml-auto flex size-3.25 shrink-0 items-center justify-center text-primary">
        <ContextMenuPrimitive.ItemIndicator>
          <Check size={13} aria-hidden />
        </ContextMenuPrimitive.ItemIndicator>
      </span>
    </ContextMenuPrimitive.CheckboxItem>
  );
}

function ContextMenuSubTrigger({
  className = '',
  inset,
  children,
  ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.SubTrigger> & { inset?: boolean }) {
  return (
    <ContextMenuPrimitive.SubTrigger
      data-slot="context-menu-sub-trigger"
      className={cn(
        contextMenuItemVariants({ inset }),
        'data-[state=open]:bg-accent data-[state=open]:text-accent-foreground',
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRight size={13} aria-hidden className="ml-auto text-muted-foreground" />
    </ContextMenuPrimitive.SubTrigger>
  );
}

function ContextMenuSeparator({
  className = '',
  ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.Separator>) {
  return (
    <ContextMenuPrimitive.Separator
      data-slot="context-menu-separator"
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  );
}

export {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
};
